import React, { forwardRef, useImperativeHandle } from "react";
import renderOptionText from "../../utils/renderOptionText";
import { Box, Typography } from "@mui/material";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {
  pdfSliceAndSave,
  injectPageBreakSpacers,
  removePageBreakSpacers,
} from "./pdfUtils";

const COLORS = {
  primary: "#ED1C2E",
  darkAccent: "#263842",
  grey1: "#50545D",
  grey2: "#9AA4AF",
  grey3: "#C4C4C4",
  accent: "#009CCC",
  highlight: "#FFCC32",
  white: "#FFFFFF",
};

const FilledFormPDF = forwardRef(
  ({ responses = [], userName, font = "Gotham, sans-serif" }, ref) => {
    useImperativeHandle(ref, () => ({
      download: async () => {
        const el = document.getElementById("pdf-content");
        if (!el) return;

        const SCALE = 1.5;
        const margins = { top: 15, bottom: 15, left: 15, right: 15 };

        // Calculate how tall one A4 page is in DOM pixels at this scale.
        // Formula mirrors pdfSliceAndSave: maxPageHeightCanvas = (availableHeightMm * canvasWidthPx) / imgWidthMm
        const A4_WIDTH_MM = 210;
        const A4_HEIGHT_MM = 297;
        const imgWidthMm = A4_WIDTH_MM - margins.left - margins.right; // 180 mm
        const canvasWidthPx = el.offsetWidth * SCALE;
        const maxPageHeightCanvas =
          ((A4_HEIGHT_MM - margins.top - margins.bottom) * canvasWidthPx) /
          imgWidthMm;
        const pageHeightDomPx = maxPageHeightCanvas / SCALE;

        // Push any block that straddles a page boundary onto the next page
        injectPageBreakSpacers(el, pageHeightDomPx);

        // Yield to the browser so it reflows the inserted spacers before capture
        await new Promise((resolve) => requestAnimationFrame(resolve));

        const canvas = await html2canvas(el, {
          scale: SCALE,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
        });

        // Restore DOM — spacers are no longer needed after the canvas is captured
        removePageBreakSpacers(el);

        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "mm",
          format: "a4",
        });
        pdfSliceAndSave(canvas, pdf, margins, "GCCHF_Filled_Form.pdf");
      },
    }));

    let currentSection = "";
    let currentSubsection = "";
    let sectionCounter = 0;
    let subsectionCounter = 0;
    let questionCounter = 0;

    return (
      <Box
        id="pdf-content"
        sx={{
          position: "fixed",
          top: 0,
          left: "200vw",
          zIndex: -9999,
          width: 1100,
          fontFamily: font,
          bgcolor: COLORS.white,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            bgcolor: COLORS.darkAccent,
            px: 4,
            py: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography
              sx={{
                color: COLORS.accent,
                fontSize: 13,
                fontFamily: font,
                textTransform: "uppercase",
                letterSpacing: 1.5,
                mb: 0.5,
              }}
            >
              City to City Asia Pacific
            </Typography>
            <Typography
              sx={{
                color: COLORS.white,
                fontSize: 24,
                fontFamily: font,
                fontWeight: "bold",
                lineHeight: 1.2,
              }}
            >
              The Gospel and Church Health Framework – Filled Form
            </Typography>
          </Box>
          <Box sx={{ textAlign: "right", flexShrink: 0, ml: 2 }}>
            {userName && (
              <Typography
                sx={{
                  color: COLORS.white,
                  fontSize: 16,
                  fontFamily: font,
                  fontWeight: "bold",
                  mb: 0.3,
                  whiteSpace: "nowrap",
                }}
              >
                {userName}
              </Typography>
            )}
            <Typography
              sx={{ color: COLORS.grey2, fontSize: 13, fontFamily: font }}
            >
              {new Date().toLocaleDateString("en-AU", {
                day: "2-digit",
                month: "long",
                year: "numeric",
              })}
            </Typography>
          </Box>
        </Box>

        {/* Body */}
        <Box sx={{ px: 4, py: 3 }}>
          {responses.map((item, index) => {
            const newSection = item.section?.name !== currentSection;
            const newSub =
              item.subsection?.name !== currentSubsection || newSection;
            if (newSection) {
              currentSection = item.section?.name;
              sectionCounter++;
              subsectionCounter = 0;
            }
            if (newSub) {
              currentSubsection = item.subsection?.name;
              subsectionCounter++;
              questionCounter = 0;
            }
            questionCounter++;

            const selectedOption = item.question?.options
              ?.sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
              .find((opt) => opt._id === item.objective?.selected_option_id);

            // Show reflection only once per subsection — on the last question of that subsection
            const isLastInSubsection =
              index === responses.length - 1 ||
              responses[index + 1]?.subsection?.id !== item.subsection?.id;

            const hasReflection =
              isLastInSubsection &&
              item.reflection &&
              (item.reflection.reflection_text || item.reflection.self_rating);

            const reflectionQuestion =
              item.reflection?.reflection_question_text ||
              item.question?.reflection_question_text ||
              item.question?.text; // fallback
            return (
              <Box key={index}>
                {/* Section Header */}
                {newSection && (
                  <Box
                    sx={{
                      mt: index === 0 ? 0 : 4,
                      mb: 2,
                      pl: 2,
                      borderLeft: `4px solid ${COLORS.primary}`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 18,
                        fontFamily: font,
                        fontWeight: "bold",
                        color: COLORS.darkAccent,
                      }}
                    >
                      {sectionCounter}. {item.section?.name}
                    </Typography>
                  </Box>
                )}

                {/* Subsection Header */}
                {newSub && item.subsection?.name && (
                  <Box
                    sx={{
                      mt: newSection ? 0 : 2.5,
                      mb: 1.5,
                      pb: 0.5,
                      borderBottom: `2px solid ${COLORS.grey3}`,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 15,
                        fontFamily: font,
                        fontWeight: "bold",
                        color: COLORS.accent,
                        flexShrink: 0,
                      }}
                    >
                      {subsectionCounter}.
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 15,
                        fontFamily: font,
                        fontWeight: "bold",
                        color: COLORS.grey1,
                        textTransform: "uppercase",
                        letterSpacing: 0.8,
                      }}
                    >
                      {item.subsection?.name}
                    </Typography>
                  </Box>
                )}

                {/* Question Block */}
                <Box data-pdf-block="true" sx={{ mb: 2, pl: 1 }}>
                  {/* Question text (objective) */}
                  {item.question?.text && (
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontFamily: font,
                        color: COLORS.darkAccent,
                        mb: 1,
                        lineHeight: 1.6,
                        fontWeight: 600,
                      }}
                    >
                      {questionCounter}. {item.question.text}
                    </Typography>
                  )}

                  {/* Selected Answer */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: hasReflection ? 1.5 : 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontFamily: font,
                        color: COLORS.grey2,
                        fontWeight: "bold",
                        textTransform: "uppercase",
                        letterSpacing: 0.6,
                        flexShrink: 0,
                      }}
                    >
                      Answer:
                    </Typography>
                    {selectedOption ? (
                      <Box
                        sx={{
                          bgcolor: COLORS.grey3,
                          color: COLORS.grey1,
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          fontSize: 14,
                          fontFamily: font,
                          lineHeight: 1.4,
                        }}
                      >
                        {renderOptionText(selectedOption.option_text)}
                      </Box>
                    ) : (
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontFamily: font,
                          color: COLORS.grey2,
                          fontStyle: "italic",
                        }}
                      >
                        Not answered
                      </Typography>
                    )}
                  </Box>

                  {/* Reflection */}
                  {hasReflection && (
                    <Box
                      sx={{
                        mt: 1,
                        pl: 2,
                        borderLeft: `3px solid ${COLORS.highlight}`,
                        bgcolor: "#fafafa",
                        py: 1,
                        pr: 1,
                        borderRadius: "0 4px 4px 0",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontFamily: font,
                          fontWeight: "bold",
                          color: COLORS.grey1,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                          mb: 0.5,
                        }}
                      >
                        Reflection
                      </Typography>
                      {reflectionQuestion && (
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontFamily: font,
                            fontWeight: 600,
                            color: COLORS.darkAccent,
                            mb: 0.8,
                            lineHeight: 1.6,
                          }}
                        >
                          Q: {item.reflection.reflection_question_text}
                        </Typography>
                      )}
                      {item.reflection?.reflection_text && (
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontFamily: font,
                            fontStyle: "italic",
                            color: COLORS.grey1,
                            lineHeight: 1.7,
                            mb: 0.5,
                          }}
                        >
                          Answer: {""} "{item.reflection.reflection_text}"
                        </Typography>
                      )}
                      {item.reflection?.self_rating != null && (
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontFamily: font,
                            color: COLORS.grey2,
                          }}
                        >
                          Self Rating:{" "}
                          <strong>{item.reflection.self_rating}</strong> / 5
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>

                {/* Row divider */}
                <Box
                  sx={{
                    borderBottom: `1px solid ${COLORS.grey3}`,
                    mb: 1,
                    opacity: 0.5,
                  }}
                />
              </Box>
            );
          })}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            bgcolor: "#263842",
            px: 4,
            py: 2.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography
            sx={{
              fontFamily: font,
              fontSize: 13,
              color: "#9AA4AF",
              lineHeight: 1.4,
            }}
          >
            City to City Asia Pacific - The Gospel and Church Health Framework
          </Typography>

          <Typography
            sx={{
              fontFamily: font,
              fontSize: 13,
              color: "#009CCC",
              lineHeight: 1.4,
            }}
          >
            For coaching and next steps, contact CTCAP
          </Typography>
        </Box>
      </Box>
    );
  },
);

FilledFormPDF.displayName = "FilledFormPDF";

export default FilledFormPDF;
